import { lazy, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/MsalBootstrap';
import { RequireAdmin, RequireAccounting, RequireExec } from '@/auth/WhoamiContext';
import { AppShell } from '@/shell/AppShell';
import { markAppReady } from '@/lib/bootSignal';
import { screenName, trackScreen } from '@/lib/telemetry';
import { SignInGate } from '@/scenes/SignInGate';

/* Route-level code-splitting: every scene is a lazy chunk, so a user who
   opens the bids page doesn't parse the chat markdown stack, the admin
   console, or the previz editor first. Before this, all 14 scenes lived
   in one ~644 KB app chunk that every route paid for. The Suspense
   boundary lives inside AppShell (around the scene <Outlet/>) so the
   sidebar stays interactive while a scene chunk streams in. Scenes are
   named exports, hence the .then(m => ({ default: m.X })) shims. */
const HomeChat = lazy(() => import('@/scenes/HomeChat').then((m) => ({ default: m.HomeChat })));
const ToolsView = lazy(() => import('@/scenes/ToolsView').then((m) => ({ default: m.ToolsView })));
const ProjectsHub = lazy(() => import('@/scenes/ProjectsHub').then((m) => ({ default: m.ProjectsHub })));
const ProjectView = lazy(() => import('@/scenes/ProjectView').then((m) => ({ default: m.ProjectView })));
const PreconHub = lazy(() => import('@/scenes/PreconHub').then((m) => ({ default: m.PreconHub })));
const BidScene = lazy(() => import('@/scenes/BidScene').then((m) => ({ default: m.BidScene })));
const Portfolio = lazy(() => import('@/scenes/Portfolio').then((m) => ({ default: m.Portfolio })));
const AccountingHub = lazy(() => import('@/scenes/AccountingHub').then((m) => ({ default: m.AccountingHub })));
const AdminV2 = lazy(() => import('@/scenes/AdminV2').then((m) => ({ default: m.AdminV2 })));
const TurnaroundScorecard = lazy(() => import('@/scenes/TurnaroundScorecard'));
const PrevizScene = lazy(() => import('@/scenes/PrevizScene').then((m) => ({ default: m.PrevizScene })));
const TogalMergeScene = lazy(() => import('@/scenes/TogalMergeScene').then((m) => ({ default: m.TogalMergeScene })));
const PlansStandalone = lazy(() => import('@/scenes/PlansStandalone').then((m) => ({ default: m.PlansStandalone })));
const SettingsView = lazy(() => import('@/scenes/SettingsView').then((m) => ({ default: m.SettingsView })));
const NotificationsHub = lazy(() => import('@/scenes/NotificationsHub').then((m) => ({ default: m.NotificationsHub })));
const ThreadsScene = lazy(() => import('@/scenes/ThreadsScene').then((m) => ({ default: m.ThreadsScene })));
const DocOpen = lazy(() => import('@/scenes/DocOpen'));
const TurnoverCiteOpen = lazy(() => import('@/scenes/TurnoverCiteOpen'));

/**
 * Wraps <HomeChat /> with a React `key` derived from
 * `location.state.resetKey`, so a Sidebar action (New thread / resume a
 * thread) remounts HomeChat with fresh state. The nav state also carries
 * the multi-thread intent (migration 090):
 *   - startNew: open an empty composer (lazy — nothing persists until the
 *     first message). The "New thread" button passes this.
 *   - threadId: resume this specific thread (sidebar resume).
 *   - neither: HomeChat resumes the user's most-recent global thread, so
 *     plain Home navigation returns you to your last conversation instead
 *     of silently starting a new one.
 */
/**
 * Auth guard for every route inside the app shell. Before this existed,
 * any deep link (/home bookmark, browser URL autocomplete, restored tab
 * whose per-tab sessionStorage token cache had died) rendered the full
 * shell with `user = null` — no data, no redirect to the gate, and the
 * sidebar showing the mock persona's name. Now:
 *   - mock/walkthrough mode bypasses the guard (the demo has no MSAL);
 *   - while MSAL is still resolving (including the boot ssoSilent
 *     attempt that adopts a session from another tab) we render nothing —
 *     the splash overlay covers this window;
 *   - once ready, no user → redirect to /sign-in, remembering where the
 *     user was headed so the gate can return them there after sign-in.
 */
function RequireAuth() {
  const { user, ready, mocked } = useAuth();
  const location = useLocation();
  if (mocked) return <Outlet />;
  if (!ready) return null;
  if (!user) {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  return <Outlet />;
}

function HomeChatRoute() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const st = location.state as
    | { resetKey?: number; startNew?: boolean; threadId?: string; draft?: string }
    | null;
  const resetKey = st?.resetKey ?? 0;
  // The open conversation rides the address (?t=, 2026-10-27), so a refresh
  // or back/forward reopens it: a new chat's thread was in component state
  // only, and a refresh opened a blank one. The address wins: a turn carried
  // into a new thread (thread_lost, a thread that would not bind) moves the
  // address, and the sidebar's state still names the old one. The sidebar
  // opens a thread on a bare /home, so its choice is never overridden. A
  // "new thread" request counts only when the address names none (its state
  // survives the refresh that must reopen what it became).
  const urlThread = params.get('t');
  // The address as it is now: HomeChat's bootstrap holds the first render's
  // onThreadChange, whose own params may predate the ?t= set since.
  const paramsRef = useRef(params);
  paramsRef.current = params;
  // The state as it is now, for the same reason. Both handlers below build on
  // it, so neither puts back what the other took out (a sent draft).
  const stateRef = useRef(st);
  stateRef.current = st;
  return (
    <HomeChat
      key={resetKey}
      startNew={(st?.startNew ?? false) && !urlThread}
      initialThreadId={urlThread ?? st?.threadId ?? null}
      // A hand-off draft (e.g. "flag this number" from the Financials
      // explainer) pre-fills the composer. Without startNew it is NOT
      // auto-sent — the user reads it and presses send.
      initialDraft={st?.draft ?? ''}
      onThreadChange={(id) => {
        if (paramsRef.current.get('t') === id) return;
        // Replace, keeping the state: the same entry, the same HomeChat key,
        // so the chat is not remounted mid-turn. null: the address named a
        // conversation this person cannot open, so it is taken out.
        // The state follows, so nothing reads the old thread back from it.
        const state = { ...(stateRef.current ?? {}), threadId: id ?? undefined };
        setParams(
          (p) => {
            const next = new URLSearchParams(p);
            if (id) next.set('t', id);
            else next.delete('t');
            return next;
          },
          { replace: true, state },
        );
      }}
      // Sent: the draft leaves the state (the same entry and address), or a
      // refresh would put it back in the box as if it had never been sent.
      // Kept until then, so a refresh before sending still shows it.
      onDraftSent={() => {
        if (!stateRef.current?.draft) return;
        const state = { ...stateRef.current };
        delete state.draft;
        setParams(new URLSearchParams(paramsRef.current), { replace: true, state });
      }}
    />
  );
}

/* AppReadyBeacon — tells the out-of-tree splash (separate React root, see
   splashHost) that the app behind it is real: auth settled (ready — cached
   account, ssoSilent result, or mock) AND the frame containing the first
   routed content has painted (double-rAF). The splash dismisses at
   max(brand-minimum, this signal), capped at the old fixed duration. */
function AppReadyBeacon() {
  const { ready } = useAuth();
  useEffect(() => {
    if (!ready) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => markAppReady());
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [ready]);
  return null;
}

/* ScreenBeacon — one screen view per route change, for App Insights
   (lib/telemetry.ts; does nothing unless the build turned telemetry on).
   Keyed on the path and the tab or view it shows, so the chat's ?t= thread
   switch is not a new screen. "/" only redirects, so it is not one either. */
function ScreenBeacon() {
  const { pathname, search } = useLocation();
  const screen = screenName(pathname, search);
  useEffect(() => {
    if (pathname !== '/') trackScreen(pathname, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new ?t= alone is not a new screen
  }, [pathname, screen]);
  return null;
}

export default function App() {
  return (
    <>
      <AppReadyBeacon />
      <ScreenBeacon />
      <Routes>
        {/* Sign-in gate is full-bleed, no app shell. */}
        <Route path="/sign-in" element={<SignInGate />} />

        {/* Everything else is inside the Sidebar + SceneHost layout,
            behind the auth guard. */}
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route path="/home" element={<HomeChatRoute />} />
            <Route path="/tools" element={<ToolsView />} />
            <Route path="/tools/togal-merge" element={<TogalMergeScene />} />
            <Route path="/tools/previz" element={<PrevizScene />} />
            <Route path="/plans" element={<PlansStandalone />} />
            <Route path="/projects" element={<ProjectsHub />} />
            <Route path="/projects/:projectId" element={<ProjectView />} />
            <Route path="/bids" element={<PreconHub />} />
            {/* Slice 1B per-bid workspace. Behind VITE_BIDSCENE_ENABLED so
                the flag-off env falls back to the legacy "navigate to
                /home with ?bid=" behavior from BidsHub. */}
            {import.meta.env.VITE_BIDSCENE_ENABLED === 'true' && (
              <Route path="/bids/:bidId" element={<BidScene />} />
            )}
            {/* Executives get the full Portfolio; accounting staff (e.g. the
                Controller) pass this guard too — RequireAccounting is the
                exec-OR-accounting union — but the scene locks non-execs to the
                Financials tab (Portfolio.tsx finOnly). Ops endpoints stay
                require_executive at the API; the /financials family is
                require_accounting_staff — that split is the enforcement. */}
            <Route element={<RequireAccounting />}>
              <Route path="/portfolio" element={<Portfolio />} />
            </Route>
            {/* Accounting domain hub — accounting staff or execs. Shell hidden
                from others by direct URL; every /tools/accounting/* endpoint is
                also require_accounting_staff at the API (the real boundary). */}
            <Route element={<RequireAccounting />}>
              <Route path="/accounting" element={<AccountingHub />} />
            </Route>
            <Route element={<RequireAdmin />}>
              <Route path="/admin" element={<AdminV2 />} />
            </Route>
            {/* Ball-in-court review turnaround — private to management. RequireExec
                gates the shell; every /tools/turnaround/* endpoint is also
                admin-or-executive at the API (the real boundary). */}
            <Route element={<RequireExec />}>
              <Route path="/turnaround" element={<TurnaroundScorecard />} />
            </Route>
            <Route path="/notifications" element={<NotificationsHub />} />
            <Route path="/threads" element={<ThreadsScene />} />
            {/* Deep-link document opener — the https target for citations in
                Word reports / inbox alerts (/doc/<id>?page=N). Two routes
                because the project/bid PK namespaces overlap. */}
            <Route path="/doc/:documentId" element={<DocOpen kind="project" />} />
            <Route path="/biddoc/:documentId" element={<DocOpen kind="bid" />} />
            <Route path="/tcite/:turnoverId/:ref" element={<TurnoverCiteOpen />} />
            <Route path="/settings" element={<SettingsView />} />
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/sign-in" replace />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </>
  );
}
